import { Modal, ModalBody, ModalTitle, ModalFooter, MODAL_INNER_GAP } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import Hairline from 'design-system/src/components/Hairline/Hairline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Drug, Patient, client } from '../../api';
import { ExtraInfoTable } from '../ExtraInfoTable/ExtraInfoTable';
import { useRef, useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import { StandardErrorToast, Toast } from 'design-system/src/components/Toast/Toast';
import { LoadingSpinner } from 'design-system/src/components/LoadingSpinner/LoadingSpinner';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { LetterEditorInput } from '../tableItemDetailViewComponents/FormComponents';

type AppealLetterResponse = {
  letter_contents: string;
  denial_id: string;
};

type AppealEditModalProps = {
  closeModal: () => void;
  patient: Patient;
  drug: Drug;
  documentId: string;
  setDocumentId: (documentId: string) => void;
  taskId: string;
};

const AppealEditModal = ({ closeModal, patient, drug, documentId, setDocumentId, taskId }: AppealEditModalProps) => {
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [denialId, setDenialId] = useState<string>('');

  const letterQuery = useQuery<AppealLetterResponse>({
    queryKey: ['appeal-letter', documentId],
    queryFn: async () => {
      const response = await client.get(`appeal-letters/${documentId}`);
      return response.data;
    },
  });

  useEffect(() => {
    if (letterQuery.data) {
      if (!letterQuery.data.letter_contents || !letterQuery.data.denial_id) {
        toast.custom(() => <StandardErrorToast />);
        Sentry.captureException(new Error('Appeal letter content or denial ID not found'));
        return;
      }
      setInputValue(letterQuery.data.letter_contents);
      setDenialId(letterQuery.data.denial_id);
    }
  }, [letterQuery.data]);

  const regenerate = useMutation({
    mutationFn: async () => {
      const data = {
        appeal_document_id: documentId,
        denial_id: denialId,
        rx_id: drug.id,
        updated_letter_content: inputValue,
        task_id: taskId,
      };

      const response = await client.post('regenerate-prior-auth-letter', data);
      return response.data;
    },
    onSuccess: (data) => {
      toast.custom(() => <Toast variant="success">Appeal letter updated successfully.</Toast>);
      queryClient.invalidateQueries({ queryKey: ['appeal-letter', documentId] });
      // Set the document ID in the state so that we don't have to reload all the task data
      setDocumentId(data.new_letter_document_id);
      closeModal();
    },
    onError: (error) => {
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
    },
  });

  const extraInfoData = [
    { label: 'Patient', value: `${patient?.first_name} ${patient?.last_name}` },
    { label: 'Drug', value: drug?.name },
  ];

  return (
    <Modal onClose={closeModal} wide>
      <ModalTitle>Edit appeal letter</ModalTitle>

      <ModalBody>
        <ExtraInfoTable
          data={extraInfoData}
          minFieldLabelWidth="80px"
          marginTop="0"
          tableMarginBottom={MODAL_INNER_GAP}
          hairlineComponent={<Hairline $marginTop="0" $marginBottom="0" />}
        />
        <Typography>
          Please note that this text will be inserted into the letter template shown on the page. You can save and
          preview the final PDF before submitting.
        </Typography>
        {letterQuery.isLoading ? (
          // TODO: Make a better loading state here so that the height doesn't change when the text appears
          <LoadingSpinner height="200px" />
        ) : (
          <LetterEditorInput
            ref={inputRef}
            placeholder="Update appeal letter content..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
          />
        )}
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" disabled={regenerate.isPending} onClick={closeModal}>
          Cancel
        </Button>
        <Button
          onClick={() => regenerate.mutate()}
          isLoading={regenerate.isPending}
          disabled={letterQuery.isLoading || !inputValue.trim()}
        >
          Save & preview PDF
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AppealEditModal;
